/**
 * Phase 129 (Plan 129-06, D-08, D-10, D-11, D-20) — the Robot card of the project Engineering tab.
 *
 * The project's Robot settings, edited through the same editor Bot Admin uses
 * (`ProjectQueueConfigEditor`, mode `engineering`): enabled, source columns, cadence, daily budget,
 * the triager's mode / WIP limit / approval cap, the Factory company and project, and the
 * repository rows with their provisioning status and Retry. There is no SRE section, no project
 * picker, no all-projects table, no Disable and no catalog Refresh here, and the triager reading is
 * the redacted one (counts only, D-08).
 *
 * The card saves on its own (D-20): it never touches the General form, and because the tab panel
 * stays mounted its unsaved edits survive a tab switch. Rendered only on an instance with the
 * Robot / Factory add-on.
 */
import type { ReactElement } from 'react';
import {
  ProjectQueueConfigEditor,
  RepositoryProvisioningList,
  type QueueConfigEditorSaveInput,
} from '../bot-admin/ProjectQueueConfigEditor';
import {
  useProjectRobotView,
  useRetryProjectProvisioning,
  useSaveProjectQueueConfig,
} from '../../hooks/useProjectEngineering';
import { useRobotEnabled } from '../../hooks/useRobotEnabled';
import type { ProjectEngineeringQueueConfigInput } from '../../services/api/projectEngineering';

export interface ProjectRobotCardProps {
  projectId: number;
}

/**
 * T-129-21: pick the member keys one by one, so nothing else the editor might carry can reach the
 * wire. The server rejects any other key with a 400 as well.
 */
function toMemberInput(input: QueueConfigEditorSaveInput): ProjectEngineeringQueueConfigInput {
  const body: ProjectEngineeringQueueConfigInput = {
    enabled: input.enabled,
    sourceTaskStateIds: input.sourceTaskStateIds,
    pollIntervalSeconds: input.pollIntervalSeconds,
    dailyBudget: input.dailyBudget,
    triagerMode: input.triagerMode,
    triagerWipLimit: input.triagerWipLimit,
    triagerApprovalCap: input.triagerApprovalCap,
    paperclipCompanyId: input.paperclipCompanyId,
  };
  if (input.paperclipProjectId !== undefined) body.paperclipProjectId = input.paperclipProjectId;
  if (input.repositories !== undefined) body.repositories = input.repositories;
  return body;
}

function RobotCardBody({ projectId }: ProjectRobotCardProps): ReactElement {
  const viewQ = useProjectRobotView(projectId);
  const saveM = useSaveProjectQueueConfig(projectId);
  const retryM = useRetryProjectProvisioning(projectId);
  const view = viewQ.data;

  return (
    <section
      className="bg-white rounded-lg border border-gray-200 p-6"
      data-section="engineering-robot"
    >
      <h2 className="text-lg font-semibold text-gray-900">Robot</h2>
      <p className="mt-0.5 text-sm text-gray-500">
        Turn the Robot on for this project, pick which columns feed it, and set cadence, daily
        budget and the Factory™ project it builds in.
      </p>
      <div className="mt-4">
        {viewQ.isLoading ? (
          <p className="text-sm text-gray-400">Loading Robot settings…</p>
        ) : viewQ.isError || !view ? (
          <p className="text-sm text-red-600" role="alert">
            Failed to load the Robot settings.
          </p>
        ) : (
          <ProjectQueueConfigEditor
            mode="engineering"
            projectId={projectId}
            row={view.config}
            projectStates={view.taskStates}
            catalog={view.catalog}
            onSave={(input, options) => saveM.mutate(toMemberInput(input), options)}
            savePending={saveM.isPending}
            saveError={saveM.isError ? (saveM.error as Error).message : null}
            renderProvisioning={(entries) => (
              <RepositoryProvisioningList
                entries={entries}
                onRetry={(repository) => retryM.mutate(repository)}
                retryingRepository={retryM.isPending ? (retryM.variables ?? '') : null}
                retryFailed={retryM.isError}
              />
            )}
          />
        )}
      </div>
    </section>
  );
}

export function ProjectRobotCard({ projectId }: ProjectRobotCardProps): ReactElement | null {
  const robotEnabled = useRobotEnabled();
  if (!robotEnabled) return null;
  return <RobotCardBody projectId={projectId} />;
}

export default ProjectRobotCard;
